/**
 * PageLoader — VM Display branded in-page loader for API/data loading states.
 * Shows the VM Display logo, an animated row of mini-screens (video-wall
 * pulse), and a progress bar.
 *
 * Usage:
 *   if (loading) return <PageLoader />;
 *   if (loading) return <PageLoader height="40vh" />;
 *   if (loading) return <PageLoader height="30vh" compact />;
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import LogoMain from '../../Images/logo_main_black.webp';
import { NAVY, NAVY_DARK, GOLD, GOLD_SOFT } from '../../theme/tokens';

interface PageLoaderProps {
  height?: string;
  message?: string;
  compact?: boolean;
}

/**
 * One mini-screen tile in the animated video-wall row. Each tile is a
 * small NAVY bezel with a coloured "content" surface that fades up,
 * then continuously pulses with a staggered delay so the row reads
 * left-to-right like a wall of displays coming online.
 */
const ScreenTile: React.FC<{
  delay: string;
  tint: string;
  glyph: React.ReactNode;
}> = ({ delay, tint, glyph }) => (
  <Box
    sx={{
      width: 28,
      height: 20,
      borderRadius: '4px',
      bgcolor: NAVY_DARK,
      border: `1px solid ${NAVY}`,
      boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.04)`,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      animation:
        'plTilePop 0.4s ease-out forwards, plTilePulse 2s ease-in-out infinite',
      animationDelay: `${delay}, ${parseFloat(delay) + 0.8}s`,
      '@keyframes plTilePop': {
        '0%': { opacity: 0, transform: 'scale(0.6)' },
        '70%': { transform: 'scale(1.08)' },
        '100%': { opacity: 1, transform: 'scale(1)' },
      },
      '@keyframes plTilePulse': {
        '0%, 100%': { boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.04)` },
        '50%': {
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.08), 0 0 8px ${tint}`,
        },
      },
      // Tiny stand below the bezel
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -3,
        left: '50%',
        width: 10,
        height: 2,
        bgcolor: NAVY,
        borderRadius: 1,
        transform: 'translateX(-50%)',
      },
    }}
  >
    {/* The screen surface */}
    <Box
      sx={{
        width: 22,
        height: 14,
        borderRadius: '2px',
        background: `linear-gradient(135deg, ${tint} 0%, ${NAVY_DARK} 120%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,0.85)',
        animation: 'plScreenFlick 2s ease-in-out infinite',
        animationDelay: `${parseFloat(delay) + 0.8}s`,
        '@keyframes plScreenFlick': {
          '0%, 100%': { opacity: 0.7 },
          '50%': { opacity: 1 },
        },
      }}
    >
      {glyph}
    </Box>
  </Box>
);

const PageLoader: React.FC<PageLoaderProps> = ({
  height = '60vh',
  message = 'Loading display data...',
  compact = false,
}) => {
  const logoSize = compact ? 100 : 140;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={height}
      sx={{ userSelect: 'none' }}
    >
      {/* Logo with float animation */}
      <Box
        sx={{
          position: 'relative',
          mb: compact ? 1.5 : 2.5,
          animation: 'plFloat 2.4s ease-in-out infinite',
          '@keyframes plFloat': {
            '0%, 100%': { transform: 'translateY(0)' },
            '50%': { transform: 'translateY(-6px)' },
          },
        }}
      >
        {/* Soft gold glow behind the logo */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: logoSize * 0.8,
            height: logoSize * 0.8,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${GOLD}1f 0%, transparent 70%)`,
            animation: 'plGlow 2.4s ease-in-out infinite',
            '@keyframes plGlow': {
              '0%, 100%': {
                opacity: 0.35,
                transform: 'translate(-50%, -50%) scale(1)',
              },
              '50%': {
                opacity: 1,
                transform: 'translate(-50%, -50%) scale(1.25)',
              },
            },
          }}
        />
        <img
          src={LogoMain}
          alt="VM display"
          style={{
            width: logoSize,
            height: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        />
      </Box>

      {/* Video-wall row */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 2.5,
            mt: 0.5,
            animation: 'plFadeUp 0.6s ease-out',
            '@keyframes plFadeUp': {
              '0%': { opacity: 0, transform: 'translateY(6px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <ScreenTile
            delay="0s"
            tint="#3b82f6"
            glyph={
              // Image glyph
              <svg width="11" height="9" viewBox="0 0 24 24" fill="none">
                <path d="M5 18l4-5 3 4 3-3 4 4H5zm12-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" fill="currentColor" />
              </svg>
            }
          />
          <ScreenTile
            delay="0.15s"
            tint="#7c3aed"
            glyph={
              // Video play glyph
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            }
          />
          <ScreenTile
            delay="0.3s"
            tint={GOLD}
            glyph={
              // Live dot
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                  boxShadow: '0 0 6px currentColor',
                }}
              />
            }
          />
          <ScreenTile
            delay="0.45s"
            tint="#10b981"
            glyph={
              // Playlist lines
              <svg width="10" height="8" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="5" width="14" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="11" width="11" height="2" rx="1" fill="currentColor" />
                <rect x="3" y="17" width="14" height="2" rx="1" fill="currentColor" />
              </svg>
            }
          />
          <ScreenTile
            delay="0.6s"
            tint="#0e7c9b"
            glyph={
              // Broadcast waves
              <svg width="10" height="9" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 13a1 1 0 100-2 1 1 0 000 2zM7.8 16.2a6 6 0 010-8.4M16.2 7.8a6 6 0 010 8.4M5 18.6a9 9 0 010-13.2M19 5.4a9 9 0 010 13.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            }
          />
        </Box>
      )}

      {/* Progress bar */}
      <Box
        sx={{
          width: compact ? 90 : 130,
          height: 3,
          borderRadius: 2,
          bgcolor: '#e2e8f0',
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            width: '35%',
            height: '100%',
            borderRadius: 2,
            background: `linear-gradient(90deg, ${NAVY}, ${GOLD}, ${NAVY})`,
            backgroundSize: '200% 100%',
            animation: 'plSlide 1.4s ease-in-out infinite',
            '@keyframes plSlide': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(350%)' },
            },
          }}
        />
      </Box>

      {/* Message with signage icons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          opacity: 0.5,
          animation: 'plMsgIn 0.8s ease-out 0.3s both',
          '@keyframes plMsgIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 0.5 },
          },
        }}
      >
        {/* Monitor / display icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 2H3a1 1 0 00-1 1v13a1 1 0 001 1h7v2H7v2h10v-2h-3v-2h7a1 1 0 001-1V3a1 1 0 00-1-1zm-1 13H4V4h16v11z"
            fill="#94a3b8"
          />
        </svg>
        <Typography
          sx={{
            fontSize: 10,
            color: '#94a3b8',
            fontWeight: 500,
            letterSpacing: '0.04em',
          }}
        >
          {message}
        </Typography>
        {/* Cast / broadcast icon */}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path
            d="M1 18v3h3a3 3 0 00-3-3zm0-4v2a5 5 0 015 5h2a7 7 0 00-7-7zm0-4v2a9 9 0 019 9h2A11 11 0 001 10zm20-7H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"
            fill="#94a3b8"
          />
        </svg>
      </Box>
    </Box>
  );
};

export { PageLoader };
export default PageLoader;
